// src/components/devices/DeviceEditor.js
import React,{ useContext,useEffect,useState } from 'react';
import { toast } from 'react-toastify';
import { FaTimes, FaPlus } from 'react-icons/fa';
import { AppContext } from '../../context/AppContext';
import './DeviceEditor.css';
import { useAuth } from '../../context/AuthContext';
const DeviceEditor = ({ device, onClose }) => {
  const { devices, updateDeviceMetadata, refreshDevices } = useContext(AppContext);
  const [metadata, setMetadata] = useState({ 
    locations: [],
    deviceTypes: [],
    floors: []
  });
  
  const API_BASE_URL = '';
  const API_PATH = '/api';
  const { apiRequest } = useAuth();

  // Fetch metadata from API
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const {response,data} = await apiRequest(`${API_BASE_URL}${API_PATH}/metadata`);
        if (response.ok) {
          // const data = await response.json();
          setMetadata(data);
        }
      } catch (error) {
        console.error('Error fetching metadata:', error);
      }
    };
    
    fetchMetadata();
  }, []);
  
  // Get unique locations from existing devices
  const getAllLocations = () => {
    const locationSet = new Set();
    devices.forEach(d => {
      if (d.location && d.location.trim()) locationSet.add(d.location);
    });
    return [...locationSet].sort();
  };
  
  // Get device types from metadata, fallback to extracting from devices
  const getAllDeviceTypes = () => {
    if (metadata.deviceTypes && metadata.deviceTypes.length > 0) {
      return metadata.deviceTypes;
    }
    
    // Fallback to extracting from devices
    const typeSet = new Set();
    devices.forEach(d => {
      if (d.type) typeSet.add(d.type);
    });
    return [...typeSet];
  };
  
  // Get floors from metadata, fallback to extracting from devices
  const getAllFloors = () => {
    if (metadata.floors && metadata.floors.length > 0) {
      return metadata.floors;
    }
    
    // Fallback to extracting from devices
    const floorSet = new Set();
    devices.forEach(d => {
      if (d.floor) floorSet.add(d.floor);
    });
    return [...floorSet];
  };
  
  const [formData, setFormData] = useState({
    name: device.name || '',
    location: device.location || '',
    type: device.type || '',
    floor: device.floor || '',
    newLocation: '',
    newType: '',
    newFloor: '',
    isAddingLocation: false,
    isAddingType: false,
    isAddingFloor: false
  });
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Toggle adding new location
  const toggleAddLocation = () => {
    setFormData(prev => ({ 
      ...prev, 
      isAddingLocation: !prev.isAddingLocation,
      newLocation: ''
    }));
  };
  
  // Toggle adding new type
  const toggleAddType = () => {
    setFormData(prev => ({ 
      ...prev, 
      isAddingType: !prev.isAddingType,
      newType: ''
    }));
  };
  
  // Toggle adding new floor
  const toggleAddFloor = () => {
    setFormData(prev => ({ 
      ...prev, 
      isAddingFloor: !prev.isAddingFloor,
      newFloor: ''
    }));
  };
  
  // Add new location
  const addNewLocation = () => {
    if (!formData.newLocation.trim()) {
      toast.error('Location name cannot be empty');
      return;
    }
    
    const newLocationName = formData.newLocation.trim();
    
    // Check if location already exists
    const existingLocations = getAllLocations();
    if (existingLocations.includes(newLocationName)) {
      toast.error('Location already exists');
      return;
    }
    
    setFormData(prev => ({ 
      ...prev, 
      location: newLocationName,
      newLocation: '',
      isAddingLocation: false
    }));
    
    toast.success(`Location "${newLocationName}" will be added when you save the device`);
  };
  
  // Add new type
  const addNewType = () => {
    if (!formData.newType.trim()) {
      toast.error('Type name cannot be empty');
      return;
    }
    
    setFormData(prev => ({ 
      ...prev, 
      type: prev.newType,
      isAddingType: false
    }));
  };
  
  // Add new floor
  const addNewFloor = () => {
    if (!formData.newFloor.trim()) {
      toast.error('Floor name cannot be empty');
      return;
    }
    
    const newFloorName = formData.newFloor.trim();
    
    // Check if floor already exists
    const existingFloors = getAllFloors();
    if (existingFloors.includes(newFloorName)) {
      toast.error('Floor already exists');
      return;
    }
    
    setFormData(prev => ({ 
      ...prev, 
      floor: newFloorName,
      newFloor: '',
      isAddingFloor: false
    }));
    
    toast.success(`Floor "${newFloorName}" will be added when you save the device`);
  };
  
  // Handle save
  const handleSave = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare metadata
      const metadata = {
        name: formData.name,
        location: formData.location,
        type: formData.type,
        floor: formData.floor
      };
      
      // Update device metadata
      await updateDeviceMetadata(device.id, metadata);
      
      // Refresh the device list to show updated data
      await refreshDevices();
      
      // Show success message
      toast.success(`Device ${metadata.name} updated successfully`);
      
      // Close the editor
      onClose();
    } catch (error) {
      console.error('Error saving device:', error);
      toast.error('Failed to update device');
    }
  };
  
  return (
    <div className="device-editor">
      <div className="editor-header">
        <h3>Edit Device</h3>
        <button className="close-button" onClick={onClose}>
          <FaTimes />
        </button>
      </div>
      
      <form onSubmit={handleSave}>
        <div className="form-group">
          <label htmlFor="name">Device Name</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="location">Location</label>
          {!formData.isAddingLocation ? (
            <div className="select-with-add">
              <select
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
              >
                <option value="">Select Location</option>
                {getAllLocations().map(loc => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
              <button 
                type="button" 
                className="add-button"
                onClick={(e) => {
                  e.stopPropagation(); // Add this line
                  toggleAddLocation();
                }}
                title="Add new location"
              >
                <FaPlus />
              </button>
            </div>
          ) : (
            <div className="new-value-input">
              <input
                type="text"
                name="newLocation"
                value={formData.newLocation}
                onChange={handleChange}
                placeholder="Enter new location"
              />
              <div className="button-group">
                <button 
                  type="button" 
                  className="add-button"
                  onClick={addNewLocation}
                >
                  Add
                </button>
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={toggleAddLocation}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
        
        <div className="form-group">
          <label htmlFor="type">Device Type</label>
          {!formData.isAddingType ? (
            <div className="select-with-add">
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
              >
                <option value="">Select Type</option>
                {getAllDeviceTypes().map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <button 
                type="button" 
                className="add-button"
                onClick={toggleAddType}
                title="Add new type"
              >
                <FaPlus />
              </button>
            </div>
          ) : (
            <div className="new-value-input">
              <input
                type="text"
                name="newType"
                value={formData.newType}
                onChange={handleChange}
                placeholder="Enter new type"
              />
              <div className="button-group">
                <button 
                  type="button" 
                  className="add-button"
                  onClick={addNewType}
                >
                  Add
                </button>
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={toggleAddType}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
        
        <div className="form-group">
          <label htmlFor="floor">Floor</label>
          {!formData.isAddingFloor ? (
            <div className="select-with-add">
              <select
                id="floor"
                name="floor"
                value={formData.floor}
                onChange={handleChange}
              >
                <option value="">Select Floor</option>
                {getAllFloors().map(floor => (
                  <option key={floor} value={floor}>{floor}</option>
                ))}
              </select>
              <button 
                type="button" 
                className="add-button"
                onClick={toggleAddFloor}
                title="Add new floor"
              >
                <FaPlus />
              </button>
            </div>
          ) : (
            <div className="new-value-input">
              <input
                type="text"
                name="newFloor"
                value={formData.newFloor}
                onChange={handleChange}
                placeholder="Enter new floor"
              />
              <div className="button-group">
                <button 
                  type="button" 
                  className="add-button"
                  onClick={addNewFloor}
                >
                  Add
                </button>
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={toggleAddFloor}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
        
        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="save-button">
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default DeviceEditor;